import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import ProductsApi from "api/products";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import StockApi from "api/stock";
import Footer from "examples/Footer";
import WarehouseApi from "api/warehouse";
import { useTranslation } from "react-i18next";
import SupplierApi from "api/supplier";
import Icon from "@mui/material/Icon";

/**
 * `IncreaseStock` component provides a form to increase stock quantities for products.
 * Users can select a product and specify the quantity to increase.
 */
function IncreaseStock() {
  const navigate = useNavigate(); // Hook for navigation
  const { t } = useTranslation();

  // State for holding stock information (product ID and quantity)
  const [stockItems, setStockItems] = useState([
    {
      id: Date.now(),
      product_id: null,
      quantity: "",
      unit_price: "",
      supplier: [],
      warehouse_id: null,
      date: new Date().toISOString().split("T")[0],
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [products, setProducts] = useState([]);
  const [errors, setErrors] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const token = localStorage.getItem("token");

  useEffect(() => {
    WarehouseApi.GetWarehouseList(token)
      .then((response) => {
        setWarehouses(response.data);
        setLoadingWarehouses(false);
      })
      .catch((error) => {
        console.error("Error fetching warehouse list:", error);
        setLoadingWarehouses(false);
      });
  }, [token]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found in localStorage.");
      return;
    }

    setLoading(true);
    ProductsApi.GetAllProducts(token, false)
      .then((response) => {
        setProducts(response.data);
      })
      .catch((error) => {
        console.error("Error fetching products:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Fetch suppliers from the API
  useEffect(() => {
    const fetchSuppliers = async () => {
      const token = localStorage.getItem("token");
      try {
        const supplierData = await SupplierApi.GetSuppliersList(token);
        setSuppliers(supplierData.data.results);
      } catch (error) {
        console.error("Failed to fetch suppliers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, []);

  const validate = () => {
    let valid = true;
    const newErrors = {};

    stockItems.forEach((item, index) => {
      if (!item.product_id) {
        newErrors[`product_id_${index}`] = `${t("Le produit est requis !")}`;
        valid = false;
      }
      if (!item.quantity) {
        newErrors[`quantity_${index}`] = `${t("La quantité est requise !")}`;
        valid = false;
      }
      if (!item.unit_price) {
        newErrors[`unit_price_${index}`] = `${t("Le prix unitaire est requis !")}`;
        valid = false;
      }
      if (!item.warehouse_id) {
        newErrors[`warehouse_id_${index}`] = `${t("L'entrepôt est requis !")}`;
        valid = false;
      }
      if (!item.date) {
        newErrors[`date_${index}`] = `${t("La date est requise !")}`;
        valid = false;
      }
    });

    setErrors(newErrors);
    return valid;
  };

  const handleAddItem = () => {
    setStockItems([
      ...stockItems,
      {
        id: Date.now(),
        product_id: null,
        quantity: "",
        unit_price: "",
        supplier: [],
        warehouse_id: null,
        date: new Date().toISOString().split("T")[0],
      },
    ]);
  };

  const handleRemoveItem = (id) => {
    if (stockItems.length > 1) {
      setStockItems(stockItems.filter((item) => item.id !== id));
    }
  };

  const handleItemChange = (id, field, value) => {
    setErrors({});
    setStockItems(
      stockItems.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleSave = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found in localStorage.");
      return;
    }

    if (!validate()) {
      return;
    }

    setLoadingSave(true);

    const payload = {
      new_stock_items: stockItems.map((item) => ({
        product_id: item.product_id.value,
        quantity: Number(item.quantity),
        unit_price: Number(item.unit_price),
        warehouse_id: item.warehouse_id.value,
        date: item.date,
        // If supplier information is available, process it (handle both cases)
        ...(item.supplier && Array.isArray(item.supplier) && {
          supplier_ids: item.supplier.map((s) => s.value),
        }),
      })),
    };

    StockApi.IncreaseStock(payload, token)
      .then((response) => {
        navigate("/gestion-de-stock/situation-de-stock");
      })
      .catch((error) => {
        console.error(error.response?.data?.msg || error.message);
      })
      .finally(() => {
        setLoadingSave(false);
      });
  };

  const handleCreateSupplier = () => {
    navigate("/gestion-de-stock/nouveau-fournisseur");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        minHeight="85vh"
      >
        <SoftBox py={3} mt={5}>
          <Grid container justifyContent="center" sx={{ height: "100%" }}>
            <Grid item xs={12}>
              <Card elevation={3} sx={{ borderRadius: "16px" }}>
                <CardContent sx={{ p: 4 }}>
                  <SoftBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p={3}
                  >
                    <SoftTypography variant="h6" fontWeight="medium">
                      {t("Augmenter le stock")}
                    </SoftTypography>
                    <SoftButton
                      variant="gradient"
                      color="info"
                      onClick={handleAddItem}
                    >
                      <Icon sx={{ fontWeight: "bold", mr: 1 }}>add</Icon>
                      {t("Ajouter un produit")}
                    </SoftButton>
                  </SoftBox>

                  <SoftBox mt={3}>
                    {stockItems.map((item, index) => (
                      <Grid container spacing={2} key={item.id} alignItems="flex-start" sx={{
                        mb: 3,
                        pb: 3,
                        borderBottom: index !== stockItems.length - 1 ? "1px solid #f0f2f5" : "none"
                      }}>
                        {/* Produit */}
                        <Grid item xs={12} sm={6} md={3}>
                          <SoftBox display="flex" flexDirection="column">
                            <SoftBox ml={0.5} mb={0.5} sx={{ display: "flex", alignItems: "center", height: "24px" }}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {t("Produit")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftSelect
                              label={t("Produit")}
                              placeholder={t("Sélectionner un produit")}
                              value={item.product_id}
                              onChange={(newValue) =>
                                handleItemChange(item.id, "product_id", newValue)
                              }
                              options={
                                loading
                                  ? [
                                    {
                                      value: "",
                                      label: (
                                        <CircularProgress
                                          size={20}
                                          sx={{
                                            color: "#1F51FF",
                                            marginLeft: 7,
                                          }}
                                        />
                                      ),
                                    },
                                  ]
                                  : (products || []).length > 0
                                    ? products.map((product) => ({
                                      value: product.id,
                                      label: product.product_name,
                                    }))
                                    : [
                                      {
                                        value: "",
                                        label: `${t("Aucun produit disponible")}`,
                                      },
                                    ]
                              }
                            />
                            {errors[`product_id_${index}`] && (
                              <SoftTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                                {errors[`product_id_${index}`]}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </Grid>

                        {/* Quantité */}
                        <Grid item xs={12} sm={3} md={1.25}>
                          <SoftBox display="flex" flexDirection="column">
                            <SoftBox ml={0.5} mb={0.5} sx={{ display: "flex", alignItems: "center", height: "24px" }}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {t("Quantité")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftInput
                              type="number"
                              placeholder={t("Qté")}
                              value={item.quantity}
                              onChange={(e) => {
                                const value = Math.max(1, e.target.value);
                                handleItemChange(item.id, "quantity", value);
                              }}
                              error={!!errors[`quantity_${index}`]}
                            />
                            {errors[`quantity_${index}`] && (
                              <SoftTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                                {errors[`quantity_${index}`]}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </Grid>

                        {/* Prix unitaire */}
                        <Grid item xs={12} sm={3} md={1.25}>
                          <SoftBox display="flex" flexDirection="column">
                            <SoftBox ml={0.5} mb={0.5} sx={{ display: "flex", alignItems: "center", height: "24px" }}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {t("Prix u.")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftInput
                              type="number"
                              placeholder={t("Prix")}
                              value={item.unit_price}
                              onChange={(e) => {
                                const value = Math.max(1, e.target.value);
                                handleItemChange(item.id, "unit_price", value);
                              }}
                              error={!!errors[`unit_price_${index}`]}
                            />
                            {errors[`unit_price_${index}`] && (
                              <SoftTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                                {errors[`unit_price_${index}`]}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </Grid>

                        {/* Entrepôt */}
                        <Grid item xs={12} sm={6} md={1.75}>
                          <SoftBox display="flex" flexDirection="column">
                            <SoftBox ml={0.5} mb={0.5} sx={{ display: "flex", alignItems: "center", height: "24px" }}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {t("Entrepôt")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftSelect
                              label={t("Entrepôt")}
                              placeholder={t("Entrepôt")}
                              options={(warehouses || []).map((warehouse) => ({
                                value: warehouse.id,
                                label: warehouse.name || warehouse.warehouse_name,
                              }))}
                              value={item.warehouse_id}
                              onChange={(value) =>
                                handleItemChange(item.id, "warehouse_id", value)
                              }
                              loading={loadingWarehouses}
                            />
                            {errors[`warehouse_id_${index}`] && (
                              <SoftTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                                {errors[`warehouse_id_${index}`]}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </Grid>

                        {/* Date */}
                        <Grid item xs={12} sm={6} md={1.75}>
                          <SoftBox display="flex" flexDirection="column">
                            <SoftBox ml={0.5} mb={0.5} sx={{ display: "flex", alignItems: "center", height: "24px" }}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {t("Date")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftInput
                              type="date"
                              value={item.date}
                              onChange={(e) =>
                                handleItemChange(item.id, "date", e.target.value)
                              }
                            />
                            {errors[`date_${index}`] && (
                              <SoftTypography variant="caption" color="error" sx={{ mt: 0.5, ml: 0.5 }}>
                                {errors[`date_${index}`]}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </Grid>

                        {/* Fournisseur */}
                        <Grid item xs={12} sm={6} md={2}>
                          <SoftBox display="flex" flexDirection="column">
                            <SoftBox ml={0.5} mb={0.5} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", height: "24px" }}>
                              <SoftTypography
                                component="label"
                                variant="caption"
                                fontWeight="bold"
                                textTransform="capitalize"
                              >
                                {t("Fournisseur")}
                              </SoftTypography>
                              <SoftTypography
                                onClick={handleCreateSupplier}
                                variant="button"
                                color="info"
                                fontWeight="medium"
                                sx={{ cursor: "pointer", fontSize: "0.65rem" }}
                              >
                                + {t("Nouveau")}
                              </SoftTypography>
                            </SoftBox>
                            <SoftSelect
                              label={t("Fournisseur")}
                              placeholder={t("Fournisseur")}
                              value={item.supplier}
                              onChange={(newValue) =>
                                handleItemChange(item.id, "supplier", newValue)
                              }
                              options={(suppliers || []).map((supplier) => ({
                                value: supplier.id,
                                label: supplier.name,
                              }))}
                              isMulti
                            />
                          </SoftBox>
                        </Grid>

                        {/* Supprimer */}
                        <Grid item xs={12} sm={1} md={1}>
                          <SoftBox
                            mt={3.5}
                            display="flex"
                            justifyContent="center"
                          >
                            <SoftTypography
                              variant="body1"
                              color="error"
                              sx={{
                                cursor: "pointer",
                                lineHeight: 0,
                                p: 1,
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#fff5f5" }
                              }}
                              onClick={() => handleRemoveItem(item.id)}
                            >
                              <Icon>delete</Icon>
                            </SoftTypography>
                          </SoftBox>
                        </Grid>
                      </Grid>
                    ))}
                  </SoftBox>

                  <SoftBox mt={5} display="flex" justifyContent="flex-end">
                    <SoftButton
                      disabled={loadingSave}
                      variant="gradient"
                      color="dark"
                      onClick={handleSave}
                      sx={{ px: 5, borderRadius: "8px" }}
                    >
                      {loadingSave ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        `${t("Enregistrer")}`
                      )}
                    </SoftButton>
                  </SoftBox>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </SoftBox>
        <Footer />
      </SoftBox>
    </DashboardLayout>
  );
}

export default IncreaseStock;
